import React, { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  AlertTitle,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Stack,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Collapse
} from '@mui/material';
import {
  Upload as UploadIcon,
  Download as DownloadIcon,
  Warning as WarningIcon,
  Close as CloseIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../../services/adminService';
import * as XLSX from 'xlsx';

const ValidationResults = ({ results, onProceed, loading }) => {
  const [expanded, setExpanded] = useState(true);

  if (!results) return null;

  const hasIssues = results.validation_issues?.length > 0;
  const canProceed = results.can_proceed !== false;

  return (
    <Card sx={{ mt: 3 }}>
      <CardHeader
        title={
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={1}>
              {hasIssues && <WarningIcon color="warning" />}
              <Typography variant="h6">
                {hasIssues ? 'Validation Issues Found' : 'Validation Complete'}
              </Typography>
            </Box>
            <IconButton onClick={() => setExpanded(!expanded)}>
              {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            </IconButton>
          </Box>
        }
      />
      <Collapse in={expanded}>
        <CardContent>
          <Stack spacing={2}>
            {/* Summary */}
            <Box>
              <Typography variant="body1" gutterBottom>
                <strong>Summary:</strong>
              </Typography>
              <Typography variant="body2" color="text.secondary">
                • {results.valid_teams_count || 0} teams are ready to create
                {hasIssues && (
                  <>
                    <br />• {results.validation_issues.length} teams have issues
                  </>
                )}
              </Typography>
            </Box>

            {/* Validation Issues */}
            {hasIssues && (
              <Box>
                <Typography variant="subtitle1" color="warning.main" gutterBottom>
                  Issues Found:
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Team</TableCell>
                      <TableCell>Issues</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.validation_issues.map((issue, idx) => (
                      <TableRow key={idx}>
                        <TableCell>{issue.team_name}</TableCell>
                        <TableCell>
                          <ul style={{ margin: 0, paddingLeft: 16 }}>
                            {issue.issues.map((iss, issIdx) => (
                              <li key={issIdx}>
                                <Typography variant="body2" color="error">
                                  {iss}
                                </Typography>
                              </li>
                            ))}
                          </ul>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

            {/* Action Button */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
              {canProceed && (
                <Button
                  variant="contained"
                  onClick={onProceed}
                  disabled={loading}
                  startIcon={loading ? <LinearProgress /> : null}
                >
                  {loading ? 'Creating Teams...' : `Create ${results.valid_teams_count || 0} Teams`}
                </Button>
              )}
            </Box>
          </Stack>
        </CardContent>
      </Collapse>
    </Card>
  );
};

const BulkTeamUpload = ({ open, onClose, onSuccess }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isUploading, setIsUploading] = useState(false);
  const [validationResults, setValidationResults] = useState(null);
  const [teamsData, setTeamsData] = useState(null);

  const resetStates = () => {
    setValidationResults(null);
    setTeamsData(null);
    setIsUploading(false);
  };

  const handleClose = () => {
    if (!isUploading) {
      resetStates();
      onClose();
    }
  };

  // Download template
  const downloadTemplate = () => {
    const templateData = [
      {
        'Team Name': 'Example Team 1',
        'Funder': 'Example Foundation',
        'School UUIDs': 'uuid1,uuid2,uuid3'
      },
      {
        'Team Name': 'Example Team 2', 
        'Funder': 'Another Foundation',
        'School UUIDs': 'uuid4,uuid5'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Teams');
    XLSX.writeFile(wb, 'bulk_team_upload_template.xlsx');
  };

  // Handle file upload
  const handleFileUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      setIsUploading(true);
      setValidationResults(null);

      // Parse Excel file
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      if (jsonData.length === 0) {
        enqueueSnackbar('No data found in file', { variant: 'error' });
        return;
      }

      // Transform data to expected format
      const transformedTeams = jsonData.map((row, index) => {
        const teamData = {
          name: row['Team Name']?.toString().trim(),
          funder: row['Funder']?.toString().trim() || null,
          school_uuids: []
        };

        // Parse school UUIDs
        if (row['School UUIDs']) {
          const uuids = row['School UUIDs'].toString().split(',')
            .map(uuid => uuid.trim())
            .filter(uuid => uuid.length > 0);
          teamData.school_uuids = uuids;
        }

        return teamData;
      });

      setTeamsData(transformedTeams);

      // Validate with backend
      const response = await adminService.bulkCreateTeams(transformedTeams, false, enqueueSnackbar);
      setValidationResults(response);

    } catch (error) {
      console.error('Error processing file:', error);
      enqueueSnackbar('Error processing file: ' + error.message, { variant: 'error' });
    } finally {
      setIsUploading(false);
      // Reset file input
      event.target.value = '';
    }
  }, [enqueueSnackbar]);

  // Proceed with creation
  const handleProceed = async () => {
    if (!teamsData) return;

    try {
      setIsUploading(true);

      const response = await adminService.bulkCreateTeams(teamsData, true, enqueueSnackbar);
      
      if (response.status === 'success') {
        enqueueSnackbar(
          `Successfully created ${response.created_count} teams` +
          (response.failed_count > 0 ? ` (${response.failed_count} failed)` : ''),
          { variant: 'success' }
        );
        
        if (onSuccess) {
          onSuccess();
        }
        handleClose();
      } else {
        enqueueSnackbar('Unexpected response from server', { variant: 'error' });
      }

    } catch (error) {
      console.error('Error creating teams:', error);
      enqueueSnackbar('Error creating teams: ' + error.message, { variant: 'error' });
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Bulk Team Upload</Typography>
          <IconButton onClick={handleClose} disabled={isUploading}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={3}>
          {/* Instructions */}
          <Alert severity="info">
            <AlertTitle>Upload Instructions</AlertTitle>
            <Typography variant="body2">
              1. Download the template file below<br />
              2. Fill in team information with school UUIDs (comma-separated)<br />
              3. Upload the completed file for validation<br />
              4. Review any issues and proceed with creation
            </Typography>
          </Alert>

          {/* Template Download */}
          <Box>
            <Button
              variant="outlined"
              startIcon={<DownloadIcon />}
              onClick={downloadTemplate}
              disabled={isUploading}
            >
              Download Template
            </Button>
          </Box>

          {/* File Upload */}
          <Box>
            <input
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileUpload}
              disabled={isUploading}
              style={{ display: 'none' }}
              id="bulk-upload-input"
            />
            <label htmlFor="bulk-upload-input">
              <Button
                variant="contained"
                component="span"
                startIcon={<UploadIcon />}
                disabled={isUploading}
              >
                {isUploading ? 'Processing...' : 'Upload File'}
              </Button>
            </label>
          </Box>

          {/* Loading */}
          {isUploading && (
            <Box>
              <LinearProgress />
              <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
                Processing file...
              </Typography>
            </Box>
          )}

          {/* Validation Results */}
          <ValidationResults
            results={validationResults}
            onProceed={handleProceed}
            loading={isUploading}
          />
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose} disabled={isUploading}>
          {validationResults ? 'Cancel' : 'Close'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default BulkTeamUpload;